import axios from "axios";
import { resolveApiUrl } from "../assets/GlobalVariables";
import { getSessionId } from "./authService";

async function withSession(body = {}) {
  const sessionID = await getSessionId();
  if (!sessionID) {
    throw new Error("ai_sales_no_session");
  }
  return { ...body, sessionID: String(sessionID).trim() };
}

async function post(path, body, opts = {}) {
  const payload = await withSession(body);
  const { data } = await axios.post(resolveApiUrl(path), payload, {
    withCredentials: true,
    timeout: opts.timeout ?? 120000,
    headers: { "Content-Type": "application/json" },
  });
  if (data && data.status === "fail") {
    const err = new Error(
      typeof data.message === "string" && data.message.trim()
        ? data.message.trim()
        : "ai_sales_request_failed"
    );
    err.aiSalesPayload = data;
    throw err;
  }
  return data;
}

/**
 * @returns {Promise<{ status: string, message?: string, payload: object }>}
 */
export async function createAiSalesLead(fields) {
  return post("/api/ai-sales/leads/create", fields);
}

export async function listAiSalesLeads(params = {}) {
  return post("/api/ai-sales/leads/list", params);
}

export async function getAiSalesLeadDetail(leadId) {
  return post("/api/ai-sales/leads/detail", { lead_id: leadId });
}

export async function patchAiSalesLead(leadId, fields) {
  return post("/api/ai-sales/leads/patch", { lead_id: leadId, ...fields });
}

export async function addAiSalesLeadMessage(leadId, messageText, senderType = "agent", payloadJson) {
  return post("/api/ai-sales/leads/message", {
    lead_id: leadId,
    message_text: messageText,
    sender_type: senderType,
    payload_json: payloadJson,
  });
}

export async function qualifyAiSalesLead(leadId) {
  return post("/api/ai-sales/leads/qualify", { lead_id: leadId });
}

/**
 * @param {string} leadId
 * @param {object|object[]|undefined} selectedResultOrDeals — אופציונלי: דיל מנורמל (כמו selectedResult) או מערך דילים; מדלג על searchLds בשרת
 * @returns {Promise<{ status: string, message?: string, payload: { offers: object[], ai_recommended_offers?: object[], recommendation_run_id?: string|null, ai_message?: { short_text: string, full_text: string, cta_text: string }, warning?: string } }>}
 * `ai_recommended_offers`: עד 3 המלצות — package (חתכי LDS), final_price, reason (עברית), ai_recommendation (score/breakdown).
 * `recommendation_run_id` / `ai_message`: שלב 2 — snapshot ב-DB (אחרי מיגרציה) והודעה דטרמיניסטית ללקוח.
 */
export async function generateAiSalesLeadOffers(leadId, selectedResultOrDeals) {
  const body = { lead_id: leadId };
  if (selectedResultOrDeals != null) {
    body.selected_result = selectedResultOrDeals;
  }
  return post("/api/ai-sales/leads/generate-offers", body, { timeout: 180000 });
}

/**
 * Rebuild customer message from stored recommendation snapshot (same deterministic builder as generate-offers).
 * @param {string} leadId
 * @param {string} [recommendationRunId]
 */
export async function refreshAiSalesRecommendationMessage(leadId, recommendationRunId) {
  const body = { lead_id: leadId };
  if (recommendationRunId) {
    body.recommendation_run_id = recommendationRunId;
  }
  return post("/api/ai-sales/leads/recommendation-message/refresh", body);
}

export async function updateAiSalesLeadStatus(leadId, status) {
  return post("/api/ai-sales/leads/status", { lead_id: leadId, status });
}

export async function assignAiSalesLead(leadId, agentId) {
  return post("/api/ai-sales/leads/assign", { lead_id: leadId, agent_id: agentId });
}

export async function deleteAiSalesLead(leadId) {
  return post("/api/ai-sales/leads/delete", { lead_id: leadId });
}
